import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Stamp, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import AvatarDisplay from "./profile/AvatarDisplay";

/**
 * CosignButton — public "I vouch for this investigation" stamp.
 *
 * Used on AI Dossier pages and Connection pages. Toggleable per signed-in
 * user. The button shows total count + your status (stamped or not). When
 * the author disabled cosigns at publish, the button renders disabled and
 * shows the explanation.
 *
 * Props:
 *   targetType — 'deep_dive' | 'connection'
 *   targetId   — string
 *   authorId   — string user_id, used to hide button on own work
 */
export default function CosignButton({ targetType, targetId, authorId }) {
  const { user } = useAuth();
  const [state, setState] = useState({ count: 0, my_cosigned: false, top_cosigners: [], allowed: true });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!targetId) return;
    let cancel = false;
    setLoading(true);
    api.get(`/cosigns/${targetType}/${targetId}`)
      .then((r) => { if (!cancel) setState(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [targetType, targetId]);

  const toggle = async () => {
    if (submitting) return;
    if (!user) { toast.error("Sign in to co-sign"); return; }
    if (!state.allowed) { toast.error("Cosigns are disabled by the author"); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/cosigns/${targetType}/${targetId}`);
      setState((s) => ({ ...s, count: r.data.count, my_cosigned: r.data.my_cosigned }));
      toast.success(r.data.my_cosigned ? "Co-signed" : "Co-sign removed");
      // Refresh top cosigners list
      api.get(`/cosigns/${targetType}/${targetId}`).then((r2) => setState(r2.data)).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSubmitting(false);
    }
  };

  // Hide on own work — authors can see their cosign count via the header below.
  const isOwn = user && authorId && user.id === authorId;

  if (loading) {
    return (
      <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] text-zinc-500">
        <Loader2 size={11} className="animate-spin" /> COSIGNS…
      </div>
    );
  }

  return (
    <div data-testid="cosign-block" className="inline-flex flex-col items-start gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        {!isOwn && (
          <button
            type="button"
            onClick={toggle}
            disabled={submitting || !state.allowed}
            data-testid="cosign-toggle-btn"
            title={!state.allowed ? "Cosigns disabled by the author" : (state.my_cosigned ? "Remove your co-sign" : "Vouch for this investigation")}
            className={`inline-flex items-center gap-2 px-3 py-1.5 border font-mono text-[10px] tracking-[0.2em] uppercase transition-colors ${
              state.my_cosigned
                ? "bg-[#00ff88] text-[#050505] border-[#00ff88] hover:bg-white"
                : state.allowed
                  ? "border-[#00ff88]/60 text-[#00ff88] hover:bg-[#00ff88]/10"
                  : "border-white/10 text-zinc-600 cursor-not-allowed"
            } disabled:opacity-50`}
          >
            <Stamp size={11} className={state.my_cosigned ? "rotate-[-12deg]" : ""} />
            {submitting ? "…" : state.my_cosigned ? "CO-SIGNED" : "CO-SIGN"}
            <span className="font-bold ml-1">{state.count}</span>
          </button>
        )}
        {isOwn && (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#00ff88]/40 text-[#00ff88] font-mono text-[10px] tracking-[0.2em] uppercase">
            <Stamp size={11} />
            CO-SIGNED BY {state.count}
          </div>
        )}
      </div>
      {state.top_cosigners?.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap font-mono text-[9px] tracking-[0.15em] text-zinc-500" data-testid="cosign-top-list">
          <span>VOUCHED BY</span>
          <div className="flex -space-x-1.5">
            {state.top_cosigners.slice(0, 5).map((u) => (
              <Link
                key={u.username}
                to={`/u/${u.username}`}
                title={`@${u.username}`}
                className="inline-block w-5 h-5 rounded-full border border-[#050505] overflow-hidden bg-[#0a0a0a] hover:scale-110 transition-transform"
              >
                <AvatarDisplay avatarId={u.avatar_id} username={u.username} size={20} />
              </Link>
            ))}
          </div>
          {state.count > state.top_cosigners.length && (
            <span>+{state.count - state.top_cosigners.length} OTHERS</span>
          )}
        </div>
      )}
      {!state.allowed && !state.my_cosigned && (
        <div className="font-mono text-[9px] tracking-[0.2em] text-zinc-600">
          // AUTHOR DISABLED CO-SIGNS
        </div>
      )}
    </div>
  );
}
